import Phaser from 'phaser';
import { tokens } from '../design/tokens';
import { palette } from '../design/palette';
import { gameState } from '../state/GameState';
import { Lives } from '../state/Lives';
import { LOGICAL_WIDTH, RENDER_SCALE } from '../dimensions';
import { NUM_LEVELS } from '../state/levels';
import { createMuteButton } from '../ui/MuteButton';
import { JoystickOverlay } from '../game/JoystickOverlay';
import type { GameScene } from './GameScene';

export class HudScene extends Phaser.Scene {
  private levelText!: Phaser.GameObjects.Text;
  private bestText!: Phaser.GameObjects.Text;
  private lifeDots: Phaser.GameObjects.Arc[] = [];
  private joystickOverlay?: JoystickOverlay;

  constructor() { super('Hud'); }

  create(): void {
    this.cameras.main.setOrigin(0, 0).setZoom(RENDER_SCALE);
    // LEVEL chip (top-left)
    this.drawChipLabel(20, 16, 'LEVEL');
    this.levelText = this.add.text(20, 30, `1 / ${NUM_LEVELS}`, this.hudValueStyle()).setOrigin(0, 0);

    // Life dots under the LEVEL chip — 4 small filled circles, ball-colored.
    const dotY = 50;
    const dotR = 4;
    const dotGap = 12;
    for (let i = 0; i < Lives.MAX; i++) {
      const dot = this.add.circle(20 + dotR + i * dotGap, dotY, dotR, palette.ballCore, 1);
      dot.setDepth(1);
      this.lifeDots.push(dot);
    }

    // BEST chip (top-right)
    this.drawChipLabel(LOGICAL_WIDTH - 76, 16, 'BEST');
    this.bestText = this.add.text(LOGICAL_WIDTH - 76, 30, '1', this.hudValueStyle()).setOrigin(0, 0);

    // Below the BEST chip on the right edge — clears top-center punch-hole
    // cameras (Pixel, Galaxy S) that were hiding the button at the previous
    // top-center placement.
    createMuteButton(this, LOGICAL_WIDTH - 48, 56);

    this.joystickOverlay = new JoystickOverlay(this, 50);

    this.game.events.on('lavascend:state', () => this.refresh());
    this.game.events.on('lavascend:lives', () => this.animateLifeLoss());
    this.refreshLives();
    this.refresh();

    this.events.once('shutdown', () => {
      this.game.events.off('lavascend:state');
      this.game.events.off('lavascend:lives');
      this.joystickOverlay?.destroy();
      this.joystickOverlay = undefined;
    });
  }

  update(_time: number, delta: number): void {
    if (!this.joystickOverlay) return;
    const game = this.scene.get('Game') as GameScene | undefined;
    const snaps = game?.getJoystickSnapshots?.() ?? [];
    this.joystickOverlay.update(snaps, delta);
  }

  private refresh(): void {
    this.levelText.setText(`${gameState.level} / ${NUM_LEVELS}`);
    this.bestText.setText(`${gameState.best}`);
  }

  private refreshLives(): void {
    const remaining = gameState.lives.remaining;
    for (let i = 0; i < this.lifeDots.length; i++) {
      const lit = i < remaining;
      this.lifeDots[i].setAlpha(lit ? 1 : 0.3);
      this.lifeDots[i].setScale(lit ? 1 : 0.6);
    }
  }

  private animateLifeLoss(): void {
    const remaining = gameState.lives.remaining;
    // The dot at index `remaining` is the one that just went out.
    const dot = this.lifeDots[remaining];
    if (!dot) { this.refreshLives(); return; }
    this.tweens.add({
      targets: dot,
      alpha: { from: 1, to: 0.3 },
      scale: { from: 1, to: 0.6 },
      duration: tokens.motion.lifeLoss.dimMs,
      ease: tokens.motion.ease.emphasized,
    });
  }

  private hudValueStyle(): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      fontFamily: tokens.type.hud.family,
      fontSize: `${tokens.type.hud.size + 2}px`,
      color: tokens.colors.ui.text,
      fontStyle: `${tokens.type.hud.weight}`
    };
  }

  private drawChipLabel(x: number, y: number, label: string): void {
    const r = this.add.rectangle(x, y, 56, 14, palette.uiBgChip, 0.7)
      .setOrigin(0, 0)
      .setStrokeStyle(1, palette.uiAccent, 0.3);
    r.setDepth(0);
    this.add.text(x + 6, y + 2, label, {
      fontFamily: tokens.type.hud.family,
      fontSize: '9px',
      color: tokens.colors.ui.textMuted,
      fontStyle: '700',
      letterSpacing: 1
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0, 0);
  }

}
